import React, { useState, useEffect, useRef } from 'react';
import { Search, UserCheck, Phone, MapPin } from 'lucide-react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ClientSearch = ({ token, language, onSelect }) => {
  const pt = language === 'pt';
  const [q, setQ] = useState('');
  const [results, setResults] = useState([]);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const boxRef = useRef(null);

  useEffect(() => {
    if (q.trim().length < 2) { setResults([]); return; }
    const t = setTimeout(async () => {
      setLoading(true);
      try {
        const { data } = await axios.get(`${API}/admin/known-clients`, { params: { q }, headers: { Authorization: `Bearer ${token}` } });
        setResults(data.clients); setOpen(true);
      } catch (_) { setResults([]); }
      finally { setLoading(false); }
    }, 250);
    return () => clearTimeout(t);
  }, [q, token]);

  useEffect(() => {
    const h = (e) => { if (boxRef.current && !boxRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', h);
    return () => document.removeEventListener('mousedown', h);
  }, []);

  return (
    <div ref={boxRef} className="relative" data-testid="client-search">
      <div className="flex items-center gap-2 bg-emerald-50 border border-emerald-200 rounded-lg px-3 py-2">
        <Search size={16} className="text-emerald-600 flex-shrink-0" />
        <input value={q} onChange={e => setQ(e.target.value)} onFocus={() => results.length && setOpen(true)} data-testid="client-search-input"
          placeholder={pt ? 'Cliente existente? Procure por nome, telefone, email ou morada…' : 'Existing client? Search by name, phone, email or address…'}
          className="flex-1 bg-transparent text-sm focus:outline-none placeholder:text-emerald-700/60" />
        {loading && <span className="w-4 h-4 border-2 border-emerald-500 border-t-transparent rounded-full animate-spin" />}
      </div>
      {open && results.length > 0 && (
        <ul className="absolute z-20 mt-1 w-full bg-white border rounded-xl shadow-xl max-h-72 overflow-y-auto" data-testid="client-search-results">
          {results.map((c, i) => (
            <li key={`${c.phone}-${i}`}>
              <button type="button" onClick={() => { onSelect(c); setQ(''); setOpen(false); }} data-testid={`client-search-result-${i}`}
                className="w-full text-left px-3 py-2.5 hover:bg-emerald-50 border-b last:border-b-0 flex items-start gap-3">
                <UserCheck size={16} className="text-emerald-600 mt-0.5 flex-shrink-0" />
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-medium text-gray-900 flex items-center gap-2 flex-wrap">{c.name}
                    {c.is_airbnb && <span className="text-[10px] px-1.5 py-0.5 rounded bg-rose-100 text-rose-700">Airbnb</span>}
                    <span className="text-[10px] px-1.5 py-0.5 rounded bg-gray-100 text-gray-600">{c.jobs} {pt ? 'serviço(s)' : 'job(s)'}</span>
                  </p>
                  <p className="text-xs text-gray-500 flex flex-wrap gap-x-3">
                    {c.phone && <span className="inline-flex items-center gap-1"><Phone size={10} /> {c.phone}</span>}
                    {(c.address || c.eircode) && <span className="inline-flex items-center gap-1 truncate"><MapPin size={10} /> {c.address} {c.eircode}</span>}
                  </p>
                  {c.last_service && <p className="text-[11px] text-gray-400">{pt ? 'Último' : 'Last'}: {c.last_service}{c.last_date ? ` · ${c.last_date}` : ''}</p>}
                </div>
              </button>
            </li>
          ))}
        </ul>
      )}
      {open && !loading && q.trim().length >= 2 && results.length === 0 && (
        <p className="absolute z-20 mt-1 w-full bg-white border rounded-xl shadow p-3 text-xs text-gray-500" data-testid="client-search-empty">{pt ? 'Nenhum cliente encontrado — preencha os dados abaixo.' : 'No client found — fill in the details below.'}</p>
      )}
    </div>
  );
};

export default ClientSearch;
